import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    UserPlusIcon,
    UserMinusIcon,
    PlusCircleIcon,
    CheckCircleIcon,
    ArrowRightIcon,
    ArrowLeftIcon,
    TrashIcon,
    PencilSquareIcon,
    DocumentTextIcon,
    CalendarDaysIcon
} from '@heroicons/react/24/outline';

const initialOnboardingSteps = [
    { id: 1, name: 'Collect signed offer letter', completed: false },
    { id: 2, name: 'Set up IT equipment and accounts', completed: false },
    { id: 3, name: 'Conduct HR orientation', completed: false },
    { id: 4, name: 'Introduce to the team', completed: false },
    { id: 5, name: 'Assign a mentor', completed: false },
];

const initialOffboardingSteps = [
    { id: 1, name: 'Conduct exit interview', completed: false },
    { id: 2, name: 'Collect company assets (laptop, ID, keys)', completed: false },
    { id: 3, name: 'Disable system access and accounts', completed: false },
    { id: 4, name: 'Finalize payroll and benefits', completed: false },
    { id: 5, name: 'Update employee records', completed: false },
];

const mockEmployees = [
    { id: 1, name: 'Alice Johnson', status: 'Onboarding', progress: 50, type: 'Onboarding', startDate: '2024-08-01' },
    { id: 2, name: 'Bob Williams', status: 'Offboarding', progress: 20, type: 'Offboarding', endDate: '2024-07-31' },
    { id: 3, name: 'Charlie Brown', status: 'Onboarding', progress: 80, type: 'Onboarding', startDate: '2024-07-25' },
    { id: 4, name: 'Diana Prince', status: 'Onboarding', progress: 100, type: 'Onboarding', startDate: '2024-07-20' },
];

const OnboardingOffboarding = () => {
    const [activeTab, setActiveTab] = useState('onboarding');
    const [onboardingList, setOnboardingList] = useState(mockEmployees.filter(e => e.type === 'Onboarding'));
    const [offboardingList, setOffboardingList] = useState(mockEmployees.filter(e => e.type === 'Offboarding'));
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [onboardingSteps, setOnboardingSteps] = useState(initialOnboardingSteps);
    const [offboardingSteps, setOffboardingSteps] = useState(initialOffboardingSteps);

    const handleSelectEmployee = (employee) => {
        setSelectedEmployee(employee);
        // Reset steps to initial state for a clean view
        setOnboardingSteps(initialOnboardingSteps.map(step => ({ ...step, completed: false })));
        setOffboardingSteps(initialOffboardingSteps.map(step => ({ ...step, completed: false })));
    };

    const handleStepToggle = (type, id) => {
        if (type === 'onboarding') {
            setOnboardingSteps(onboardingSteps.map(step => step.id === id ? { ...step, completed: !step.completed } : step));
        } else {
            setOffboardingSteps(offboardingSteps.map(step => step.id === id ? { ...step, completed: !step.completed } : step));
        }
    };

    const calculateProgress = (steps) => {
        if (steps.length === 0) return 0;
        const completedCount = steps.filter(step => step.completed).length;
        return Math.floor((completedCount / steps.length) * 100);
    };

    const progress = useMemo(() => {
        if (!selectedEmployee) return 0;
        const steps = selectedEmployee.type === 'Onboarding' ? onboardingSteps : offboardingSteps;
        return calculateProgress(steps);
    }, [selectedEmployee, onboardingSteps, offboardingSteps]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-teal-100 to-lime-100 p-8">
            <motion.div
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-7xl mx-auto"
            >
                {/* Header */}
                <div className="flex flex-col md:flex-row items-center justify-between mb-8 pb-4 border-b-2 border-green-400">
                    <h1 className="text-4xl font-extrabold text-green-800 tracking-tight mb-4 md:mb-0">
                        Onboarding & Offboarding
                    </h1>
                    <div className="flex items-center space-x-4">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 transition-colors"
                        >
                            <PlusCircleIcon className="h-5 w-5" />
                            <span className="font-semibold">Add New Employee</span>
                        </motion.button>
                    </div>
                </div>

                {/* Tabs for Onboarding & Offboarding */}
                <div className="flex space-x-4 mb-8">
                    <motion.button
                        onClick={() => { setActiveTab('onboarding'); setSelectedEmployee(null); }}
                        className={`px-6 py-2 rounded-full font-semibold transition-all ${
                            activeTab === 'onboarding'
                                ? 'bg-indigo-600 text-white shadow-lg'
                                : 'bg-white text-indigo-600 hover:bg-indigo-50 shadow'
                        }`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Onboarding
                    </motion.button>
                    <motion.button
                        onClick={() => { setActiveTab('offboarding'); setSelectedEmployee(null); }}
                        className={`px-6 py-2 rounded-full font-semibold transition-all ${
                            activeTab === 'offboarding'
                                ? 'bg-red-600 text-white shadow-lg'
                                : 'bg-white text-red-600 hover:bg-red-50 shadow'
                        }`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Offboarding
                    </motion.button>
                </div>

                {/* Main Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Employee List */}
                    <motion.div
                        className="lg:col-span-1 bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                    >
                        <h3 className="text-2xl font-bold text-gray-800 mb-4">
                            {activeTab === 'onboarding' ? 'New Hires' : 'Departing Employees'}
                        </h3>
                        <AnimatePresence>
                            {(activeTab === 'onboarding' ? onboardingList : offboardingList).length > 0 ? (
                                (activeTab === 'onboarding' ? onboardingList : offboardingList).map(employee => (
                                    <motion.div
                                        key={employee.id}
                                        className={`p-4 rounded-xl cursor-pointer transition-all mb-3 ${
                                            selectedEmployee?.id === employee.id
                                                ? 'bg-indigo-100 dark:bg-indigo-900 shadow-lg border-l-4 border-indigo-600'
                                                : 'bg-gray-50 hover:bg-gray-100'
                                        }`}
                                        onClick={() => handleSelectEmployee(employee)}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                                {employee.type === 'Onboarding' ? (
                                                    <UserPlusIcon className="h-6 w-6 text-green-500" />
                                                ) : (
                                                    <UserMinusIcon className="h-6 w-6 text-red-500" />
                                                )}
                                                <span className="font-semibold text-gray-800">{employee.name}</span>
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {employee.type === 'Onboarding' ? `Start: ${employee.startDate}` : `End: ${employee.endDate}`}
                                            </div>
                                        </div>
                                    </motion.div>
                                ))
                            ) : (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="p-4 text-center text-gray-400"
                                >
                                    No employees to display.
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>

                    {/* Checklists & Progress */}
                    <motion.div
                        className="lg:col-span-2 bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                    >
                        {selectedEmployee ? (
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={selectedEmployee.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.4 }}
                                >
                                    <h3 className="text-2xl font-bold text-gray-800 mb-2">{selectedEmployee.name}'s Checklist</h3>
                                    <p className="text-sm text-gray-500 mb-4">
                                        {selectedEmployee.type === 'Onboarding' ? 'Onboarding' : 'Offboarding'} for {selectedEmployee.name}
                                    </p>

                                    {/* Progress Bar */}
                                    <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6">
                                        <div
                                            className="bg-blue-600 h-2.5 rounded-full"
                                            style={{ width: `${progress}%` }}
                                        ></div>
                                    </div>
                                    <div className="flex justify-between items-center text-sm font-medium text-gray-600 mb-6">
                                        <span>Progress:</span>
                                        <span className="text-lg font-bold text-blue-600">{progress}% Complete</span>
                                    </div>

                                    {/* Checklist Items */}
                                    <ul className="space-y-4">
                                        {(selectedEmployee.type === 'Onboarding' ? onboardingSteps : offboardingSteps).map(step => (
                                            <motion.li
                                                key={step.id}
                                                className="flex items-center justify-between p-4 bg-gray-50 rounded-xl shadow-sm"
                                                initial={{ opacity: 0, scale: 0.95 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <div className="flex items-center space-x-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={step.completed}
                                                        onChange={() => handleStepToggle(selectedEmployee.type, step.id)}
                                                        className="form-checkbox h-5 w-5 text-blue-600 rounded-full transition-colors duration-300 ease-in-out"
                                                    />
                                                    <span className={`text-lg font-medium ${step.completed ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                                                        {step.name}
                                                    </span>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} className="text-purple-600 hover:text-purple-800 transition-colors">
                                                        <PencilSquareIcon className="h-5 w-5" />
                                                    </motion.button>
                                                    <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} className="text-red-600 hover:text-red-800 transition-colors">
                                                        <TrashIcon className="h-5 w-5" />
                                                    </motion.button>
                                                </div>
                                            </motion.li>
                                        ))}
                                    </ul>
                                </motion.div>
                            </AnimatePresence>
                        ) : (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="p-10 text-center text-gray-500 rounded-xl border-dashed border-2 border-gray-300"
                            >
                                <DocumentTextIcon className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                                <p className="text-lg font-medium">Select an employee to view their checklist.</p>
                                <p className="text-sm mt-1">Click on an employee's name from the list on the left to get started.</p>
                            </motion.div>
                        )}
                    </motion.div>
                </div>
            </motion.div>
        </div>
    );
};

export default OnboardingOffboarding;